import React, { useImperativeHandle, forwardRef } from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import { useReactFlow } from "@xyflow/react";
import dagre from "dagre";

export const ReportBuilderControls = forwardRef((props, ref) => {
  const { zoomIn, zoomOut, fitView, setZoom, setNodes, setEdges, getNodes, getEdges } =
    useReactFlow();

  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const getNodeDimensions = (node) => {
    // Different node types have different sizes
    if (node.type === "section.note") {
      return { width: 250, height: 180 };
    }
    if (node.type?.startsWith("trigger.")) {
      return { width: 180, height: 60 };
    }
    // Default size for most nodes
    return { width: 200, height: 80 };
  };

  const getLayoutedElements = (nodes, edges, direction = "TB") => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Enhanced graph settings for better layout
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: 80, // Horizontal spacing between nodes
      ranksep: 100, // Vertical spacing between ranks
      marginx: 50, // Graph margins
      marginy: 50,
      align: "UL", // Alignment within ranks
      acyclicer: "greedy", // Better cycle breaking
      ranker: "tight-tree", // Better ranking algorithm
    });

    nodes.forEach((node) => {
      const dimensions = getNodeDimensions(node);
      dagreGraph.setNode(node.id, {
        width: dimensions.width,
        height: dimensions.height,
      });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    return nodes.map((node) => {
      const dagreNode = dagreGraph.node(node.id);
      const dimensions = getNodeDimensions(node);

      // Center the node position based on its actual size
      const x = dagreNode.x - dimensions.width / 2;
      const y = dagreNode.y - dimensions.height / 2;

      return {
        ...node,
        position: { x, y },
        positionAbsolute: { x, y },
      };
    });
  };

  const handleAutoLayout = () => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();

    if (currentNodes.length === 0) {
      return; // Nothing to layout
    }

    // Determine the best direction based on workflow structure
    const direction = getOptimalDirection(currentNodes, currentEdges);

    // Separate notes from other nodes for special handling
    const notes = currentNodes.filter((node) => node.type === "section.note");
    const workflowNodes = currentNodes.filter((node) => node.type !== "section.note");

    // Layout the main workflow
    const layoutedWorkflow = getLayoutedElements(workflowNodes, currentEdges, direction);

    // Position notes around the workflow without interfering
    const layoutedNotes = layoutNotes(notes, layoutedWorkflow);

    // Combine all nodes
    const allLayoutedNodes = [...layoutedWorkflow, ...layoutedNotes];

    setNodes(allLayoutedNodes);

    // Fit view with some padding and a slight delay to ensure layout is complete
    setTimeout(() => {
      fitView({ padding: 0.15 });
    }, 100);
  };

  // Expose the handleAutoLayout function via ref
  useImperativeHandle(ref, () => ({
    handleAutoLayout,
  }));

  const getOptimalDirection = (nodes, edges) => {
    // Count trigger nodes - if many triggers, use top-down
    const triggerCount = nodes.filter((n) => n.type?.startsWith("trigger.")).length;
    const totalNodes = nodes.length;

    // For workflows with multiple triggers or many nodes, use top-down
    if (triggerCount > 1 || totalNodes > 8) {
      return "TB"; // Top to Bottom
    }

    // For simpler workflows, use left to right
    return "LR"; // Left to Right
  };

  const layoutNotes = (notes, workflowNodes) => {
    if (notes.length === 0) return [];

    // Find the bounds of the workflow
    const workflowBounds = getNodesBounds(workflowNodes);

    // Position notes to the right of the workflow
    const startX = workflowBounds.maxX + 150;
    let currentY = workflowBounds.minY;

    return notes.map((note, index) => {
      const notePosition = {
        x: startX,
        y: currentY + index * 220, // Space out notes vertically
      };

      return {
        ...note,
        position: notePosition,
        positionAbsolute: notePosition,
      };
    });
  };

  const getNodesBounds = (nodes) => {
    if (nodes.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    nodes.forEach((node) => {
      const dimensions = getNodeDimensions(node);
      const x = node.position.x;
      const y = node.position.y;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + dimensions.width);
      maxY = Math.max(maxY, y + dimensions.height);
    });

    return { minX, minY, maxX, maxY };
  };

  const handleReset = () => {
    setNodes([]);
    setEdges([]);
  };

  const buttonStyle = {
    backgroundColor: isLight ? "#f5f5f5" : "#2e2e2e",
    color: isLight ? "#000" : "#fff",
    borderRadius: "6px",
    border: `1px solid ${isLight ? "#ccc" : "#555"}`,
    width: 42,
    height: 42,
    "&:hover": {
      backgroundColor: isLight ? "#e0e0e0" : "#444",
    },
  };

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 12,
        left: 22,
        display: "flex",
        flexDirection: "row",
        gap: 1,
        backgroundColor: "transparent",
        zIndex: 20,
      }}
    >
      <Tooltip title="Zoom In">
        <IconButton size="small" sx={buttonStyle} onClick={zoomIn}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Zoom Out">
        <IconButton size="small" sx={buttonStyle} onClick={zoomOut}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Clear All">
        <IconButton size="small" sx={buttonStyle} onClick={handleReset}>
          <DeleteSweepIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Auto Layout - Organize workflow intelligently">
        <IconButton size="small" sx={buttonStyle} onClick={handleAutoLayout}>
          <CleaningServicesIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});
